import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';

import { LimitedEditionNumberDto } from '../../api-client';
import { RootState } from '../../application/store';
import { LimitedEditionApiClient } from '../../providers/api-provider';
import {
	clearLimitedEditions,
	initProductLimitedEditions,
	setLimitedEditionsLoading,
} from '../../slices/LimitedEdition/limitedEditionSlice';

/**
 * Decides whether the backend has to be called for a product.
 * <p>
 * The check on the in flight set is not redundant with the loaded one: the cart can open a row while the
 * detail page is still waiting for the same product, and without a synchronous guard both would fetch.
 */
export const shouldFetchLimitedEditions = (
	productId: number,
	loadedProductIds: number[],
	inFlightProductIds: number[],
	force: boolean,
): boolean => {
	if (inFlightProductIds.includes(productId)) return false;
	if (force) return true;

	return !loadedProductIds.includes(productId);
};

// Module state, not component state: it is the only way for every place showing the selector to see
// immediately that a request is already on its way.
let inFlightProductIds: number[] = [];

/** Test only, so a case is not affected by the state left behind by the previous one. */
export const resetLimitedEditionRequestGuard = () => {
	inFlightProductIds = [];
};

export const useLimitedEditions = () => {
	const { instance } = useMsal();
	const dispatch = useDispatch();

	const numbersBySapCode = useSelector(
		(state: RootState) => state.limitedEdition.numbersBySapCode,
	);
	const loadedProductIds = useSelector(
		(state: RootState) => state.limitedEdition.loadedProductIds,
	);
	const limitedEditionsLoading = useSelector(
		(state: RootState) => state.limitedEdition.loading,
	);

	/**
	 * Loads what a product offers, once per product.
	 * @param sapCodes sap codes of the sizes on screen, so a size with no number left is told apart from one
	 * never loaded.
	 * @param excludeOrderId order being edited, whose own numbers have to stay selectable.
	 */
	const handleLimitedEditions = async (
		productId?: number,
		sapCodes: string[] = [],
		excludeOrderId?: number,
		force = false,
	) => {
		if (!productId) return;

		if (
			!shouldFetchLimitedEditions(
				productId,
				loadedProductIds,
				inFlightProductIds,
				force,
			)
		)
			return;

		inFlightProductIds = [...inFlightProductIds, productId];
		dispatch(setLimitedEditionsLoading(true));

		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.getSelectableLimitedEditions(
				productId,
				excludeOrderId,
			);

			dispatch(
				initProductLimitedEditions({
					productId,
					sapCodes,
					numbers: response.data ?? [],
				}),
			);
		} catch {
			// No notification: the pool is side information on the product page. If the backend does not
			// answer the catalogue must stay usable, and the real block is at save and at confirmation.
			dispatch(setLimitedEditionsLoading(false));
		} finally {
			inFlightProductIds = inFlightProductIds.filter((id) => id !== productId);
		}
	};

	/** Forgets the cached pool, after a conflict or when the customer changes. */
	const resetLimitedEditions = () => dispatch(clearLimitedEditions());

	/**
	 * The numbers selectable for a sap code. Undefined while it has never been loaded, empty when the sap
	 * code has no number configured: the caller uses the difference to decide between the list and the free
	 * text field.
	 */
	const numbersForSapCode = (
		sapCode?: string | null,
	): LimitedEditionNumberDto[] | undefined =>
		sapCode ? numbersBySapCode[sapCode] : undefined;

	return {
		handleLimitedEditions,
		resetLimitedEditions,
		numbersForSapCode,
		numbersBySapCode,
		loadedProductIds,
		limitedEditionsLoading,
	};
};
